import {
  type Key,
  type PropsWithChildren,
  type ReactNode,
  type ReactElement,
} from 'react';
import { isElement } from 'react-is';

import {
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

/**
 * @public
 */
export type SelectV2OptionProps<OptionValue> = PropsWithChildren &
  StylingProps &
  DataTestId & {
    value: OptionValue;

    textValue?: string;

    id?: Key;

    'aria-label'?: string;

    disabled?: boolean;
  };

/**
 * Typeguard function that will check if a ReactNode is a SelectV2.Option
 * @internal
 */
export function _isSelectV2OptionElement<T>(
  child: ReactNode,
): child is ReactElement<SelectV2OptionProps<T>> {
  if (isElement(child) && child.type === SelectV2Option) {
    return true;
  }
  return false;
}

/**
 * `SelectV2.Option` defines a single option within the select component.
 * `SelectV2.Option` components are only valid within `SelectV2.Content` or
 * `SelectV2.Group` elements.
 *
 * @example
 * ```tsx
 * <SelectV2.Content>
 *    <SelectV2.Option value="red">Red</SelectV2.Option>
 *    <SelectV2.Option value="green">Green</SelectV2.Option>
 *    <SelectV2.Group>
 *      <SelectV2.GroupLabel>Other colors</SelectV2.GroupLabel>
 *      <SelectV2.Option value="purple">Purple</SelectV2.Option>
 *    <SelectV2.Option value="pink">Pink</SelectV2.Option>
 *    </SelectV2.Group>
 * </SelectV2.Content>
 * ```
 * @public
 */
export function SelectV2Option<OptionValue>(
  props: SelectV2OptionProps<OptionValue>,
): null {
  return null;
}
